import { useCallback, useState } from "react";
import httpService from "V2.0/services/httpService";
import { showErrorToast } from "V2.0/utils";
import { CONVERSATIONS_PER_PAGE } from "../constants";

export default function usePaginatedHumanAssignedConversations(workspaceId) {
  const [state, setState] = useState({
    totalAssignedConversations: 0,
    totalAssignedConversationPages: 0,
    currentAssignedConversationPage: 1,
    assignedConversationsPageSize: CONVERSATIONS_PER_PAGE,
    assignedConversations: [],
    isAssignedConversationsLoading: true,
    isMoreAssignedConversationsLoading: false,
  });

  const setAssignedConversationsLoading = (
    isAssignedConversationsLoading,
    isMoreAssignedConversationsLoading = false
  ) => {
    setState((prevState) => {
      return {
        ...prevState,
        isAssignedConversationsLoading,
        isMoreAssignedConversationsLoading,
      };
    });
  };

  const getAssignedConversationsViewModel = (data) => {
    const { conversations, pagination } = data;
    const { page, limit, total, pages } = pagination;

    return {
      assignedConversations: conversations,
      totalAssignedConversations: total,
      totalAssignedConversationPages: pages,
      assignedConversationsPageSize: limit,
      currentAssignedConversationPage: page,
    };
  };

  const appendAssignedConversation = (
    data,
    isMoreAssignedConversationsLoading
  ) => {
    const { assignedConversations, ...restData } =
      getAssignedConversationsViewModel(data);

    setState((prevState) => {
      return {
        ...prevState,
        ...restData,
        assignedConversations: [
          ...prevState.assignedConversations,
          ...assignedConversations,
        ],
        isMoreAssignedConversationsLoading,
      };
    });
  };

  const pushAssignedConversation = (assignedConversation) => {
    setState((prevState) => {
      return {
        ...prevState,
        assignedConversations: [
          assignedConversation,
          ...prevState.assignedConversations,
        ],
      };
    });
  };

  const syncSelectedAssignedConversation = (selectedAssignedConversation) => {
    setState((prevState) => {
      const { assignedConversations } = prevState;

      const updatedAssignedConversations = assignedConversations.map(
        (assignedConversation) => {
          const { _id } = assignedConversation;
          return _id === selectedAssignedConversation._id
            ? selectedAssignedConversation
            : assignedConversation;
        }
      );

      return {
        ...prevState,
        assignedConversations: updatedAssignedConversations,
      };
    });
  };

  const setAssignedConversations = (data, isAssignedConversationsLoading) => {
    const { conversations, pagination } = data;
    const { page, limit, total, pages } = pagination;

    setState((prevState) => {
      return {
        ...prevState,
        isAssignedConversationsLoading,
        totalAssignedConversations: total,
        assignedConversations: conversations,
        assignedConversationsPageSize: CONVERSATIONS_PER_PAGE,
        currentAssignedConversationPage: page,
        totalAssignedConversationPages: pages,
      };
    });
  };

  const fetchPaginatedHumanAssignedConversations = useCallback(
    async (
      page = 1,
      pageSize = CONVERSATIONS_PER_PAGE,
      loading = true,
      isMoreLoading = false
    ) => {
      setAssignedConversationsLoading(loading, isMoreLoading);

      const params = {
        page,
        limit: pageSize,
      };

      try {
        const response = await httpService.get(
          `/conversation/whitelabelHumanAssignedConversationsPaginated/${workspaceId}`,
          { params }
        );

        if (response.status === 200) {
          const data = response.data;

          if (page === 1) {
            setAssignedConversations(data, false);
          } else {
            appendAssignedConversation(data, false);
          }
        }
      } catch (e) {
        setAssignedConversationsLoading(false, false);
        showErrorToast(e, "Failed to get conversations");
      }
    },
    [workspaceId]
  );

  const loadMoreHumanAssignedConversations = useCallback(() => {
    const {
      currentAssignedConversationPage,
      totalAssignedConversationPages,
      assignedConversationsPageSize,
    } = state;

    if (currentAssignedConversationPage < totalAssignedConversationPages) {
      fetchPaginatedHumanAssignedConversations(
        currentAssignedConversationPage + 1,
        assignedConversationsPageSize,
        false,
        true
      );
    }
  }, [
    state.currentAssignedConversationPage,
    state.totalAssignedConversationPages,
    state.assignedConversationsPageSize,
  ]);

  const showLoadMoreAssignedConversations =
    !state.isAssignedConversationsLoading &&
    state.currentAssignedConversationPage !=
      state.totalAssignedConversationPages &&
    state.totalAssignedConversationPages != 0;

  const resetPaginatedHumanAssignedConversation = () => {};

  return {
    ...state,
    fetchPaginatedHumanAssignedConversations,
    loadMoreHumanAssignedConversations,
    showLoadMoreAssignedConversations,
    syncSelectedAssignedConversation,
    pushAssignedConversation,
    resetPaginatedHumanAssignedConversation,
  };
}
